import { Box, Heading, Paragraph } from '@zillow/constellation-10';
import React from 'react';

import AuthorAvatar from './AuthorAvatar';
import AuthorCard from './AuthorCard';
import { AUTHOR_PAGE_CONTENT_MAX_WIDTH } from './AuthorPage';
import type { AuthorHeaderProps } from './AuthorPage.types';
import ExpertiseCard from './ExpertiseCard';
import SocialLinks from './SocialLinks';
// Import the shared max width constant

/**
 * Header section for the author page, including avatar, name, job title, description, and social links.
 */
const AuthorHeader: React.FC<AuthorHeaderProps> = ({ author, socialLinks }) => (
    // v8 '$colors.blue600' is SkunkWorks #001962 = v10 blue.900 exactly (see
    // COLOR_MAP textDarkBlue). Kept on the primitive rather than a semantic bg
    // token so this fixed dark banner does not invert in dark mode.
    <Box
        css={{
            width: '100%',
            backgroundColor: 'blue.900',
            paddingBlock: 'layout.looser',
            paddingInline: 'loose',
            position: 'relative',
            zIndex: 0,
            lg: {
                paddingInline: 'layout.looser',
                minHeight: '421px',
            },
            xl: {
                paddingInline: 'layout.vast.default',
            },
        }}
    >
        <Box
            css={{
                display: 'flex',
                flexDirection: 'row',
                gap: 'layout.vast.default',
                width: '100%',
                maxWidth: AUTHOR_PAGE_CONTENT_MAX_WIDTH, // Shared page content width
                justifyContent: 'center',
                marginBlock: '0',
                marginInline: 'auto',
            }}
        >
            <Box
                css={{
                    display: 'none',
                    md: {
                        display: 'block',
                    },
                    lg: {
                        width: '210px',
                    },
                    xl: {
                        width: '300px',
                    },
                }}
            >
                <Box
                    css={{
                        display: 'block',
                        lg: {
                            display: 'none',
                        },
                    }}
                >
                    <AuthorCard author={author} />
                </Box>
            </Box>
            <Box
                css={{
                    flex: 1,
                    // v8 '$breakpoints.lg' (769px) → v10 sizes.breakpoint-lg (48em = 768px).
                    maxWidth: 'breakpoint-lg',
                }}
            >
                <Box
                    css={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 'loose',
                        marginBottom: 'looser',
                        md: {
                            marginBottom: '0',
                        },
                    }}
                >
                    <Box css={{ md: { display: 'none' } }}>
                        <AuthorAvatar
                            avatar={author.avatar}
                            name={author.name}
                            size="xl"
                        />
                    </Box>
                    <Box>
                        {/* v8 <Text as="h1" fontType={{ default: 'h4', md: 'h3', lg: 'h2' }}>
                            → Heading level={1} with a responsive textStyle:
                            h4 = 24px = heading-sm and h3 = 36px = heading-md are
                            exact. h2 (48px/56px) has no exact v10 equivalent —
                            heading-lg is 44px/48px and heading-xl is 60px/72px —
                            so the desktop h1 renders 4px smaller than v8. Kept on
                            heading-lg to stay on the v10 type scale rather than
                            pinning the old pixels; flag for design if the 48px is
                            required. v8 fontColor="textWhite" → white
                            (canonical COLOR_MAP); the banner behind it is fixed dark
                            blue, so the color must not invert in dark mode.
                            v8 marginBottom { default: xs, lg: md } → { base: tighter, lg: loose }. */}
                        <Heading
                            level={1}
                            css={{
                                textStyle: {
                                    base: 'heading-sm',
                                    md: 'heading-md',
                                    lg: 'heading-lg',
                                },
                                color: 'white',
                                marginBottom: { base: 'tighter', lg: 'loose' },
                            }}
                            data-epi-type="title"
                        >
                            {author.name}
                        </Heading>
                        {/* v8 <Box as={Paragraph}> → Paragraph directly; v10 Paragraph
                            takes a css prop, so the Box wrapper is unnecessary.
                            v8 fontType { default: finePrint, md: body } → finePrint
                            = 12px = body-xs, body = 16px = body. */}
                        <Paragraph
                            css={{
                                textStyle: { base: 'body-xs', md: 'body' },
                                color: 'white',
                                marginBottom: 'loose',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 'tighter',
                                md: {
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: 'loose',
                                },
                            }}
                        >
                            {author?.jobTitle?.toUpperCase()}
                            <SocialLinks socialLinks={socialLinks} />
                        </Paragraph>
                    </Box>
                </Box>
                {/* @ts-expect-error - Paragraph requires children but we're using dangerouslySetInnerHTML */}
                <Paragraph
                    css={{
                        textStyle: { base: 'body-xs', md: 'body' },
                        color: 'white',
                        marginBottom: 'loose',
                        // Anchor.onImpact tokens: for links on dark backgrounds — default link blue is invisible on blue.900.
                        '& a': { color: 'Anchor.onImpact.default' },
                        '& a:hover': { color: 'Anchor.onImpact.hover' },
                    }}
                    // `?? ''` because author.description is optional and v10 types
                    // __html as string | TrustedHTML. Preserves the previous render
                    // (an empty paragraph) when there is no description.
                    dangerouslySetInnerHTML={{
                        __html: author.description ?? '',
                    }}
                />
                {/* Show ExpertiseCard below md breakpoint if expertise exists */}
                <Box css={{ md: { display: 'none' } }}>
                    {author.expertise && (
                        <ExpertiseCard expertise={author.expertise} />
                    )}
                </Box>
            </Box>
        </Box>
    </Box>
);

export default AuthorHeader;
